import { z } from "zod";

export const MealTypeSchema = z.enum(["breakfast", "lunch", "dinner", "snack"]);
export type MealType = z.infer<typeof MealTypeSchema>;

export const MealSourceSchema = z.enum(["manual", "manual_ai", "photo", "voice", "barcode"]);
export type MealSource = z.infer<typeof MealSourceSchema>;

// One parsed/logged food item (e.g. "kanapka z szynką").
export const MealItemSchema = z.object({
  name: z.string().min(1).max(200),
  kcal: z.number().int().min(0).max(10000),
  proteinG: z.number().min(0).max(500).nullable(),
  fatG: z.number().min(0).max(500).nullable(),
  carbsG: z.number().min(0).max(1000).nullable(),
  portionGrams: z.number().min(0).max(5000).nullable(),
  confidence: z.number().min(0).max(1),
  // Short note on what the model assumed when estimating the portion.
  // Transient — shown in the confirmation screen so the user can
  // sanity-check, not persisted. Optional+nullable: only the photo
  // parser v3 emits it; text-parse, barcode and persisted meals omit
  // it entirely.
  //
  // 400 char cap (was 200) accommodates the structured S/P/G/D/C
  // reasoning trace introduced in v3 commit after the "podwójnie /
  // potrójnie zawyżamy" report — each marker carries 10-40 chars and
  // the whole trace consistently runs 120-300 chars.
  assumptions: z.string().max(400).nullable().optional(),
  // Unit hint for the portion number (display-only): "g" for solids,
  // "ml" for drinks resolved by barcode. When absent the UI shows "g".
  // Math always stores portionGrams as a number, so unit never changes
  // calorie scaling, only the label shown to the user.
  portionUnit: z.enum(["g", "ml"]).optional(),
});
export type MealItem = z.infer<typeof MealItemSchema>;

// Output schema for the AI parser. We use this both as the prompt's
// declared contract AND as the runtime Zod validator (PROJECT.md sec 8).
export const MealParseResponseSchema = z.object({
  items: z.array(MealItemSchema).max(20),
});
export type MealParseResponse = z.infer<typeof MealParseResponseSchema>;

// === Nested meal model (ADR-0010) ===
// One ingredient inside a dish. No `confidence` — the dish-level item
// carries the confidence; ingredients are summed into the dish total.
export const IngredientSchema = z.object({
  name: z.string().min(1).max(200),
  portionGrams: z.number().min(0).max(5000).nullable(),
  kcal: z.number().int().min(0).max(10000),
  proteinG: z.number().min(0).max(500).nullable(),
  fatG: z.number().min(0).max(500).nullable(),
  carbsG: z.number().min(0).max(1000).nullable(),
});
export type Ingredient = z.infer<typeof IngredientSchema>;

// A meal item that may decompose into ingredients. `ingredients` is
// optional/empty for simple single products (a banana). When present, the
// item's kcal/macros are the sum of its ingredients.
export const NestedMealItemSchema = MealItemSchema.extend({
  ingredients: z.array(IngredientSchema).max(30).optional(),
});
export type NestedMealItem = z.infer<typeof NestedMealItemSchema>;

export const NestedMealParseResponseSchema = z.object({
  items: z.array(NestedMealItemSchema).max(20),
});
export type NestedMealParseResponse = z.infer<typeof NestedMealParseResponseSchema>;

// Persisted meal — has DB-only fields plus item data.
export const MealSchema = MealItemSchema.extend({
  id: z.string(),
  loggedAt: z.number().int(),
  mealType: MealTypeSchema.nullable(),
  source: MealSourceSchema,
});
export type Meal = z.infer<typeof MealSchema>;

// Daily summary returned by GET /meals?day=YYYY-MM-DD.
export const DailySummarySchema = z.object({
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  meals: z.array(MealSchema),
  totals: z.object({
    kcal: z.number(),
    proteinG: z.number(),
    fatG: z.number(),
    carbsG: z.number(),
  }),
});
export type DailySummary = z.infer<typeof DailySummarySchema>;

// Input for POST /meals/parse — free-form Polish text from the user.
export const MealParseInputSchema = z.object({
  text: z.string().min(2).max(2000),
  mealType: MealTypeSchema.optional(),
});
export type MealParseInput = z.infer<typeof MealParseInputSchema>;

// Input for POST /meals/photo — base64 data URL of the meal photo.
// We accept up to ~8 MB encoded (~6 MB raw) which is generous for a
// client-side resized JPEG. The frontend should pre-shrink to 1024px.
export const MealPhotoInputSchema = z.object({
  imageDataUrl: z
    .string()
    .max(8_000_000, "Zdjęcie za duże, zmniejsz rozdzielczość")
    .regex(/^data:image\/(jpeg|jpg|png|webp);base64,/, "Nieprawidłowy format obrazu"),
  mealType: MealTypeSchema.optional(),
  // Eater the photo is for. When set, parseMealPhoto switches the
  // prompt to child-portion mode and filters the recent-meals few-shot
  // to that child's history. Defaults to "self" (adult eater) when
  // omitted, which matches behaviour from before child profiles shipped.
  childId: z.string().min(1).optional(),
});
export type MealPhotoInput = z.infer<typeof MealPhotoInputSchema>;

// Input for POST /meals/voice — base64 data URL of the meal description audio.
// Whisper-large-v3-turbo handles up to ~25 MB; we cap below for safety.
export const MealVoiceInputSchema = z.object({
  audioDataUrl: z
    .string()
    .max(15_000_000, "Nagranie za długie")
    .regex(/^data:audio\/[a-z0-9+.-]+;base64,/, "Nieprawidłowy format audio"),
  mealType: MealTypeSchema.optional(),
});
export type MealVoiceInput = z.infer<typeof MealVoiceInputSchema>;

// Input for POST /meals/barcode — EAN/UPC code + optional portion grams.
// Nutrition sources return per-100g data; portionGrams scales it. When
// omitted, the resolver falls back to the product's own serving size, then
// to 100g (see resolveBarcode / ADR-0009).
export const MealBarcodeInputSchema = z.object({
  ean: z.string().regex(/^\d{8,14}$/, "EAN musi być 8-14 cyfr"),
  portionGrams: z.number().min(1).max(5000).optional(),
  mealType: MealTypeSchema.optional(),
});
export type MealBarcodeInput = z.infer<typeof MealBarcodeInputSchema>;

// Input for POST /meals — confirmed items (after user reviewed AI output).
// Items may carry an optional `ingredients` array (ADR-0010); a flat item
// with no ingredients is still valid, so this supersets MealCreateSchema.
export const MealCreateSchema = z.object({
  items: z.array(NestedMealItemSchema).min(1).max(20),
  mealType: MealTypeSchema.optional(),
  source: MealSourceSchema.default("manual"),
  rawInputText: z.string().max(2000).optional(),
  loggedAt: z.number().int().optional(),
  // Child profile this meal is for, if any (ADR-0018). Null/omitted → the
  // meal belongs to the adult logging it (the household member). The
  // server validates the child belongs to the same household.
  childId: z.string().optional(),
});
export type MealCreate = z.infer<typeof MealCreateSchema>;

// Partial update for a single ingredient (PATCH .../ingredients/:id).
export const IngredientUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  portionGrams: z.number().min(0).max(5000).nullable().optional(),
  kcal: z.number().int().min(0).max(10000).optional(),
  proteinG: z.number().min(0).max(500).nullable().optional(),
  fatG: z.number().min(0).max(500).nullable().optional(),
  carbsG: z.number().min(0).max(1000).nullable().optional(),
});
export type IngredientUpdate = z.infer<typeof IngredientUpdateSchema>;

// Input for PATCH /meals/:id — partial update of a logged meal item.
export const MealUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  kcal: z.number().int().min(0).max(10000).optional(),
  proteinG: z.number().min(0).max(500).nullable().optional(),
  fatG: z.number().min(0).max(500).nullable().optional(),
  carbsG: z.number().min(0).max(1000).nullable().optional(),
  portionGrams: z.number().min(0).max(5000).nullable().optional(),
  mealType: MealTypeSchema.nullable().optional(),
});
export type MealUpdate = z.infer<typeof MealUpdateSchema>;
